import {
  Search,
  CheckCircle,
  Hourglass,
  CheckCheck
} from 'lucide-react';
import type { Task } from '../types';

export interface WorkflowStateConfig {
  label: string;
  icon: React.ElementType;
  color: string;
  bgColor?: string;
  textColor?: string;
  buttonColor?: string;
  transitions: Array<{ to: number; label: string; color: string }>;
  requiresDocument?: boolean;
  interventionLabel?: string;
  stateInstructions?: string;
}

// Default icon and color mapping (fallback when tasks not loaded)
const defaultIconMapping: Record<number, { icon: React.ElementType; color: string; bgColor: string; textColor: string; buttonColor: string }> = {
  1: {
    icon: Search,
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    buttonColor: 'bg-blue-600 hover:bg-blue-700'
  },
  2: {
    icon: CheckCircle,
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-600',
    buttonColor: 'bg-green-600 hover:bg-green-700'
  },
  3: {
    icon: Hourglass,
    color: 'amber',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-600',
    buttonColor: 'bg-amber-600 hover:bg-amber-700'
  },
  4: {
    icon: CheckCheck,
    color: 'emerald',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-600',
    buttonColor: 'bg-emerald-600 hover:bg-emerald-700'
  },
};

// Static workflow configuration (everything except labels)
const workflowStateConfigTemplate: Record<number, Omit<WorkflowStateConfig, 'label'>> = {
  1: {
    icon: Search,
    color: 'blue',
    transitions: [{ to: 2, label: 'Valider', color: 'green' }],
    requiresDocument: false,
    interventionLabel: 'Document manquant',
    stateInstructions: 'Vérifiez les données et documents du client. Si tout est correct, validez le dossier.',
  },
  2: {
    icon: CheckCircle,
    color: 'green',
    transitions: [{ to: 3, label: 'Soumettre', color: 'amber' }],
    requiresDocument: true,
    interventionLabel: 'Problème détecté',
    stateInstructions: 'Uploadez la demande supplétive puis soumettez le dossier aux institutions. Si un problème est détecté, demandez une intervention client.',
  },
  3: {
    icon: Hourglass,
    color: 'amber',
    transitions: [{ to: 4, label: 'Clôturer', color: 'emerald' }],
    interventionLabel: 'Intervention Conseiller',
    stateInstructions: 'Traitez les réponses reçues des institutions (centrale et supplétive). Renseignez les informations puis clôturez le dossier.',
  },
  4: {
    icon: CheckCheck,
    color: 'emerald',
    transitions: [],
    stateInstructions: 'Le dossier est terminé et archivé.',
  },
};

export const colorClasses: Record<string, { bg: string; text: string; button: string }> = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-800', button: 'bg-blue-600 hover:bg-blue-700' },
  green: { bg: 'bg-green-100', text: 'text-green-800', button: 'bg-green-600 hover:bg-green-700' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-800', button: 'bg-amber-600 hover:bg-amber-700' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-800', button: 'bg-emerald-600 hover:bg-emerald-700' },
};

/**
 * Build workflow state configuration from database tasks
 * This ensures state names come from the database
 */
export function buildWorkflowStates(tasks: Task[]): Record<number, WorkflowStateConfig> {
  const config: Record<number, WorkflowStateConfig> = {};

  tasks.forEach(task => {
    const template = workflowStateConfigTemplate[task.task_number];
    if (template) {
      config[task.task_number] = {
        ...template,
        label: task.name, // ← Label comes from database!
      };
    }
  });

  return config;
}

/**
 * Get state label from tasks (database-driven)
 */
export function getStateLabel(stateNumber: number, tasks: Task[]): string {
  const task = tasks.find(t => t.task_number === stateNumber);
  return task?.name || `État ${stateNumber}`;
}

/**
 * Get icon and color config for a state
 */
export function getStateIconConfig(stateNumber: number) {
  return defaultIconMapping[stateNumber] || defaultIconMapping[1];
}

/**
 * Build task config for pipeline (database-driven labels)
 */
export function buildTaskConfig(tasks: Task[]): Record<number, { icon: React.ElementType; color: string; bgColor: string }> {
  const config: Record<number, { icon: React.ElementType; color: string; bgColor: string }> = {};

  tasks.forEach(task => {
    const iconConfig = defaultIconMapping[task.task_number];
    if (iconConfig) {
      config[task.task_number] = {
        icon: iconConfig.icon,
        color: iconConfig.textColor,
        bgColor: iconConfig.bgColor,
      };
    }
  });

  return config;
}
